import Icon from "hardcover-ui/components/Icon";
import Text from "hardcover-ui/components/Text";
import { isBookReleased, secondsFormat } from "lib/bookUtils";
import { roundRating } from "lib/numberUtils";
import pluralize from "lib/pluralize";
import BookType from "types/BookType";
import faStar from "icons/solid/star.svg";
import { dateAsWords } from "lib/dateFormat";
import {
  BookSerializersBookByline,
  EditionSerializersEditionByline,
  LetterbooksSerializersBook,
} from "types/serializers";
import {
  ReadingFormatByFormat,
  ReadingFormatByID,
} from "types/ReadingFormatType";

interface Props {
  book: BookType | LetterbooksSerializersBook | BookSerializersBookByline;
  edition?: EditionSerializersEditionByline;
  settings?: {
    showInfoClass: string;
  };
}

export default function BookHighlights({
  book,
  edition,
  settings = {
    showInfoClass: "",
  },
}: Props) {
  const pageInfo = [];

  if (edition) {
    if (edition.releaseYear) {
      pageInfo.push(edition.releaseYear);
    } else if (edition.releaseDate) {
      pageInfo.push(dateAsWords(edition.releaseDate));
    }
    pageInfo.push(ReadingFormatByID[edition.readingFormatId].format);

    if (edition.usersCount > 0) {
      pageInfo.push(
        `${edition.usersCount.toLocaleString()} ${pluralize(
          "Reader",
          edition.usersCount
        )}`
      );
    }

    if (edition.readingFormatId === ReadingFormatByFormat.audio) {
      if (edition.audioSeconds > 0) {
        pageInfo.push(secondsFormat(edition.audioSeconds));
      }
    } else {
      if (edition.pages > 0) {
        pageInfo.push(
          `${edition.pages.toLocaleString()} ${pluralize("page", edition.pages)}`
        );
      }
    }

    if (edition.editionFormat?.length > 0) {
      pageInfo.push(`Format ${edition.editionFormat}`);
    }
    if (edition.editionInformation?.length > 0) {
      pageInfo.push(`Info ${edition.editionInformation}`);
    }
    if (edition.asin) {
      pageInfo.push(edition.asin);
    }
  } else {
    const released = isBookReleased(book as BookType);
    if (book.releaseYear) {
      if (released) {
        pageInfo.push(book.releaseYear);
      } else if (book.releaseDate) {
        pageInfo.push(`Available ${dateAsWords(book.releaseDate)}`);
      } else {
        pageInfo.push(`Available in ${book.releaseYear}`);
      }
    }
    if (book.usersCount > 0) {
      pageInfo.push(
        `${book.usersCount.toLocaleString()} ${pluralize(
          "Reader",
          book.usersCount
        )}`
      );
    }

    if (book.pages > 0) {
      pageInfo.push(
        `${book.pages.toLocaleString()} ${pluralize("page", book.pages)}`
      );
    }
    if (book.audioSeconds > 0) {
      pageInfo.push(secondsFormat(book.audioSeconds));
    }
  }
  const pageData = pageInfo.join(" • ");

  return (
    <Text
      variant="secondary"
      size="sm"
      className={`font-semibold mt-2 ${settings.showInfoClass}`}
      as="p"
    >
      {pageData}
      {book.rating && (
        <>
          {" "}
          • {roundRating(book.rating, 1)}{" "}
          <Icon icon={faStar} className="fill-accent" />
        </>
      )}
    </Text>
  );
}
